import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { rankingBoards } from '@/mocks/rankings';
import { topics } from '@/mocks/topics';
import { useSeo } from '@/hooks/useSeo';

export default function RankingsPage() {
  useSeo({
    title: '榜单盘点 - 黑料社网热搜榜热梗榜精选合集榜',
    description:
      '黑料社网榜单盘点页，包含本周热搜榜、本月热梗榜与季度精选合集榜，按讨论量、渗透率与完读率三个维度排序，快速抓住当前重点。',
    keywords: '榜单盘点,热搜榜,热梗榜,精选合集榜,黑料社网榜单',
    canonicalPath: '/rankings',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '榜单盘点',
        url: 'https://jinpenge.cn/rankings',
        description: '按讨论量、渗透率与完读率排序的多个内容榜单。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jinpenge.cn/' },
          { '@type': 'ListItem', position: 2, name: '榜单盘点', item: 'https://jinpenge.cn/rankings' },
        ],
      },
      ...rankingBoards.map((board) => ({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: board.title,
        description: board.subtitle,
        itemListElement: board.items.map((item) => ({
          '@type': 'ListItem',
          position: item.rank,
          name: item.title,
        })),
      })),
    ],
  });

  return (
    <div>
      <div className="bg-foreground-950 text-background-100">
        <div className="px-4 md:px-10 py-12 md:py-16">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '榜单盘点' }]} tone="dark" />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-background-50">榜单盘点</h1>
          <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed max-w-3xl">
            专题负责归类，榜单负责排序。这里汇总黑料社网当前在更的三张榜，分别从讨论量、渗透率与完读率出发，帮你快速抓住重点。
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {rankingBoards.map((b) => (
              <a
                key={b.id}
                href={`#${b.id}`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-background-100/25 text-sm text-background-200 hover:border-accent-400 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bar-chart-horizontal-line"></i>
                {b.title}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-10 py-12 md:py-16 flex flex-col gap-14 md:gap-20">
        {rankingBoards.map((board, boardIdx) => (
          <section key={board.id} id={board.id} className="scroll-mt-32" aria-label={board.title}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 pb-5 border-b-2 border-foreground-950">
              <div className="flex items-end gap-5">
                <span className="font-heading text-4xl md:text-6xl font-black text-background-300 leading-none">
                  {String(boardIdx + 1).padStart(2, '0')}
                </span>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">{board.title}</h2>
                  <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">{board.subtitle}</p>
                </div>
              </div>
              <span className="text-xs text-foreground-500 flex items-center gap-2 whitespace-nowrap">
                <i className="ri-calendar-check-line"></i>
                {board.period}
              </span>
            </div>

            <ol className="mt-4">
              {board.items.map((item) => (
                <li
                  key={item.rank}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 py-5 border-b border-background-200 hover:bg-background-100 transition-colors px-2 -mx-2 rounded-md"
                >
                  <div className="sm:col-span-1 flex items-center gap-3">
                    <span
                      className={`w-10 h-10 rounded-md flex items-center justify-center font-heading font-black ${
                        item.rank === 1
                          ? 'bg-primary-500 text-background-50'
                          : item.rank <= 3
                          ? 'bg-accent-500 text-primary-950'
                          : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {item.rank}
                    </span>
                  </div>
                  <div className="sm:col-span-8">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-900">
                        {item.tag}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                  <div className="sm:col-span-3 sm:text-right flex sm:block items-center gap-2">
                    <span className="text-sm font-medium text-primary-600 whitespace-nowrap">{item.metric}</span>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>

      <div className="px-4 md:px-10 pb-8">
        <div className="rounded-lg bg-background-100 border border-background-200 p-7 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
              榜单之外，还有 {topics.length} 个完整专题
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-2xl">
              榜单只呈现排名靠前的条目。如果你想系统地看完同类内容，专题合集会是更合适的选择。
            </p>
          </div>
          <Link
            to="/topics"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            浏览全部专题
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}